<template>
<div
  class="checklist-table-body-row" 
  :class="{'section-zone-highlight': computedSectionZoneHighlight }"
>
  <ChecklistTableDroppableArea class="checklist-table-body-row"
    :disabled="!isSectionDroppable"
    :section="section"
    @mouseenter="handleMouseEnterSection"
    @mouseleave="handleMouseLeaveSection"
    @mouseUpDrop="onDropInSection(section)"
  >
  <div
    ref="root"
    v-bind="$attrs"
    class="checklist-table-section"
    :id="`${section.type}-${section.id}`"
    data-locator="checklist-section-row"
    :class="{ 
      'awaiting-delete-validation': section.isAwaitingValidation,
    }"
    v-click-outside="onSectionClickOutside"
    @click="toggleSectionExpansion"
  >
    <div class="checklist-table-section-inner">
      <template v-if="isEditionPending">
        <v-skeleton-loader class="checklist-table-row-skeleton" type="text"/>
      </template>
      <template v-else>
        <div class="checklist-table-first-column px-1">
          <ChecklistTableDrag 
            :dragged-item="section"
            :draggable="canDragSection"
            class="mr-auto"
          />
          <ChecklistTableSelect
            :selected="isSectionSelected"
            @select="onSelectSection"
          />
        </div>

        <div class="checklist-table-column-title">
          <AppTooltip>
            <template #activator="{ props }">
              <app-icon-ds
                :icon="isSectionExpanded ? 'caret-down' : 'caret-right'"
                :data-locator="isSectionExpanded ? 'expanded-section-icon' : 'collapsed-section-icon'"
                size="xxs"
                v-bind="props"
                weight="fas"
              />
            </template>
            {{ t(isSectionExpanded ? 'collapseTooltip' : 'expandTooltip') }}
          </AppTooltip>

          <span>{{ section.positionDisplay }}</span>

          <template v-if="isRenamingSection">
            <ChecklistTableTitleInput
              v-model.trim="renamedSectionTitle"
              data-locator="title-input"
              @enter="prepareRenameSection"
              @update:focused="onTitleInputFocusChanged"
              :type="section.type"
            />
          </template>
          <template v-else>
            <div data-locator="section-title" @click.stop="onRenameSection">
              <span v-if="computedTitle">{{ computedTitle }}</span>
              <span v-else>{{ t('renameSectionLabel') }}</span>
            </div>
          </template>

          <template v-if="!isRenamingSection">
            <span v-if="computedChildrenTaskLength" class="text-grey-700 text--small" data-locator="section-tasks-count">
              {{ computedChildrenTaskLength }}
            </span>

            <div class="d-flex">
              <ChecklistTableAddButton
                v-if="isCurrentUserPm"
                :item="section"
                :mnemo="roomMnemo"
                data-locator="add-within-section-menu-button"
              />

              <ChecklistTableSectionMenu
                @collapse-section="COLLAPSE_SECTION(section.id)"
                @delete-section="onDeleteSection"
                @expand-section="EXPAND_SECTION(section.id)"
                @rename-section="onRenameSection"
                @select-all="onSelectAll"
              />
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
  </ChecklistTableDroppableArea>

  <template v-if="isSectionExpanded">
    <ChecklistTableDroppableArea
      v-if="childrenModel.length > 0 && isSectionDroppable"
      as="div"
      class="checklist-table-body-row"
      variant="after-task"
      @mouseUpDrop="onDropAsFirstChildInSection(section)"
      :section="section"
    />
    <template v-for="child in childrenModel" :key="child.id">
      <div 
        class="checklist-table-body-row" 
        :class="{ 'drop-disabled': !isSectionDroppable, 'awaiting-delete-validation': child.isAwaitingValidation || section.isAwaitingValidation }"
      >
        <template v-if="child.type === 'title'">
          <div class="checklist-table-body-row">
            <ChecklistTableSection
              :section="child"
            />
          </div>
        </template>
        <template v-else>
          <ChecklistTableDroppableTask 
            :section="section"
            :task="child"
            @drop-after="onDropAfterTask"
          />
        </template>
      </div>
    </template>
  </template>
</div>
  <ChecklistTableDroppableArea
    v-if="enabledSubSectionGap && isSectionDroppable"
    as="div"
    class="checklist-table-body-row"
    variant="after-subsection"
    @mouseUpDrop="onDropAfterSection(section)"
  />
  <ChecklistDeleteSectionDialog
    v-if="isDeleteSectionDialogOpen"
    :is-deletion-validation-enabled="isDeletionValidationEnabled"
    :section="section"
    @close="isDeleteSectionDialogOpen = false"
    @confirm="prepareDeleteSection"
  />
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { scrollToAddedItem, scrollToMovedItemById, highlightItem } from '@/common/utils/checklist'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import ChecklistDeleteSectionDialog from '@/project/checklist/dialogs/ChecklistDeleteSectionDialog.vue'
import { CHECKLIST_OPTIONS } from '@/project/checklist/enums'
import ChecklistTableAddButton from '@/project/checklist/table/ChecklistTableAddButton.vue'
import ChecklistTableDrag from '@/project/checklist/table/ChecklistTableDrag.vue'
import ChecklistTableDroppableArea from '@/project/checklist/table/ChecklistTableDroppableArea.vue'
import ChecklistTableDroppableTask from '@/project/checklist/table/ChecklistTableDroppableTask.vue'
import ChecklistTableSectionMenu from '@/project/checklist/table/ChecklistTableSectionMenu.vue'
import ChecklistTableSelect from '@/project/checklist/table/ChecklistTableSelect.vue'
import ChecklistTableTitleInput from '@/project/checklist/table/ChecklistTableTitleInput.vue'
import { ChecklistItem, ChecklistSection } from '@/project/checklist/types'
import {
  ADD_SECTION_CHILDREN_TO_SELECTION,
  ADD_SECTION_TO_SELECTION,
  COLLAPSE_SECTION,
  DELETE_SECTION,
  EDIT_SECTION,
  EXPAND_SECTION,
  EXPAND_ALL_SECTIONS,
  GET_CHECKLIST,
  MOVE_ITEMS,
  REMOVE_SECTION_CHILDREN_FROM_SELECTION,
  REMOVE_SECTION_FROM_SELECTION,
} from '@/store/modules/checklist/action_types'
import {
  ADD_CHECKLIST_ITEM_BEING_EDITED,
  REMOVE_CHECKLIST_ITEM_BEING_EDITED,
  SET_PARENT_CHILDREN,
  SET_CHECKLIST_TITLE_INPUT_INVALID,
} from '@/store/modules/checklist/mutation_types'

type DataType = {
  isDeleteSectionDialogOpen: boolean;
  isMouseInSectionRow: boolean;
  isRenamingSection: boolean;
  lastSavedSectionTitle: string;
  renamedSectionTitle: string;
};

type moveDataType = {
  moveTo?: { id: number; type: string },
  selectedItems: Array<{ id: number; type: string }>
  withinSection?: boolean
  firstChild?: boolean
}

export default defineComponent({
  name: 'ChecklistTableSection',
  components: {
    AppTooltip,
    ChecklistTableAddButton,
    ChecklistTableDrag,
    ChecklistTableDroppableTask,
    ChecklistTableSelect,
    ChecklistDeleteSectionDialog,
    ChecklistTableTitleInput,
    ChecklistTableSectionMenu,
    ChecklistTableDroppableArea,
  },
  props: {
    section: {
      type: Object as PropType<ChecklistSection>,
      required: true,
    },
    parent: {
      type: Object as PropType<ChecklistSection>,
      required: false,
    },
    elementEntered: {
      type: String,
    },
  },
  emits: ['newElementEntered'],
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableSection' })
  },
  inject: ['checklistTableWidth'],
  data (): DataType {
    return {
      isDeleteSectionDialogOpen: false,
      isMouseInSectionRow: false,
      isRenamingSection: false,
      lastSavedSectionTitle: '',
      renamedSectionTitle: '',
    }
  },
  computed: {
    ...mapState('checklist', [
      'editSectionPending',
      'lastItemAdded',
      'searchQuery',
      'sectionsExpandState',
      'checklistDragItem',
      'checklistDragOverSection',
      'checklistTitleInputInvalid',
      'checklistDetailsPending',
    ]),
    ...mapGetters('checklist', [
      'flatChecklist',
      'isChecklistFiltered',
      'isDraggingSection',
      'isDraggingTask',
      'selectedSections',
      'prevChecklistDragItem',
    ]),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    canDragSection () {
      return this.isMouseInSectionRow && !this.isChecklistFiltered && this.isCurrentUserPm
    },
    childrenModel: {
      get () {
        return this.section.children
      },
      set (value: Array<ChecklistItem>) {
        this.SET_PARENT_CHILDREN({
          parent: this.section,
          children: value,
        })
      },
    },
    lastChild () {
      return this.section.children?.at(-1)
    },
    hasLastChildSubSection () {
      return this.lastChild?.type === 'title'
    },
    hasLastChildSubSectionDragging () {
      return this.hasLastChildSubSection && this.isDraggingSection && this.checklistDragItem?.id === this.lastChild?.id
    },
    disabledParentSectionGapForChildSectionGap () {
      return this.isSectionExpanded && this.hasLastChildSubSection && !this.hasLastChildSubSectionDragging
    },
    enabledSubSectionGap () {
      return this.isSectionDroppable && this.section.depth > 0 && !this.disabledParentSectionGapForChildSectionGap
    },
    computedSectionZoneHighlight () {
      return this.checklistDragItem && this.checklistDragOverSection?.id === this.section.id
    },
    computedChildrenTaskLength () {
      const count = this.countTasks(this.section.children)
      return count > 0
        ? `(${this.t('task', { count })})`
        : ''
    },
    computedTitle () {
      if (this.lastSavedSectionTitle) {
        return this.lastSavedSectionTitle
      }

      return this.section.title || ''
    },
    isDeletionValidationEnabled () {
      return this.currentRoom?.enableValidationChecklistItemDeletion
    },
    isEditionPending () {
      return this.editSectionPending === this.section.id
    },
    isLastSectionAdded () {
      if (this.lastItemAdded) {
        return this.lastItemAdded.id === this.section.id && this.lastItemAdded.type === 'title'
      }

      return false
    },
    isSectionSelected () {
      return this.selectedSections.some((section: ChecklistSection) => section.id === this.section.id)
    },
    isSectionExpanded () {
      const storedExpand = this.sectionsExpandState.find((section: ChecklistSection) => section.id === this.section.id)
      return storedExpand
        ? storedExpand.value
        : this.section.children.length <= CHECKLIST_OPTIONS.SECTION_AUTO_EXPAND_CHILDREN_LENGTH
    },
    isSectionDragged () {
      const draggedItemId = this.checklistDragItem?.id
      return this.section.id === draggedItemId && this.isDraggingSection
    },
    rowWidth () {
      return `${this.checklistTableWidth}px`
    },
    sectionDepthOffset () {
      return this.section.depth > 0
        ? `${20 * this.section.depth}px`
        : 0
    },
    isSectionMaxDepth () {
      return this.section.depth >= CHECKLIST_OPTIONS.SECTION_MAX_DEPTH
    },
    isSectionDroppable () {
      if (this.isDraggingSection) {
        return !this.isSectionMaxDepth && !this.section.isAwaitingValidation && !this.isSectionDragged
      }
      return !this.section.isAwaitingValidation && !this.isSectionDragged
    },
  },
  watch: {
    isLastSectionAdded (value) {
      if (value) {
        this.onLastSectionAdded()
      }
    },
    checklistDetailsPending (value) {
      if (!value && this.isLastSectionAdded) {
        this.onLastSectionAdded()
      }

      if (!value && this.$route.hash) {
        this.$nextTick(() => {
          this.handleRouteHash()
        })
      }
    },
    'section.title' (value) {
      if (value === this.lastSavedSectionTitle) {
        this.lastSavedSectionTitle = ''
      }
    },
  },
  created () {
    this.renamedSectionTitle = this.section.title
    this.SET_CHECKLIST_TITLE_INPUT_INVALID(false)
  },
  methods: {
    ...mapActions('checklist', [
      ADD_SECTION_CHILDREN_TO_SELECTION,
      ADD_SECTION_TO_SELECTION,
      COLLAPSE_SECTION,
      DELETE_SECTION,
      EDIT_SECTION,
      EXPAND_SECTION,
      EXPAND_ALL_SECTIONS,
      GET_CHECKLIST,
      MOVE_ITEMS,
      REMOVE_SECTION_CHILDREN_FROM_SELECTION,
      REMOVE_SECTION_FROM_SELECTION,
    ]),
    ...mapMutations('checklist', [
      ADD_CHECKLIST_ITEM_BEING_EDITED,
      REMOVE_CHECKLIST_ITEM_BEING_EDITED,
      SET_PARENT_CHILDREN,
      SET_CHECKLIST_TITLE_INPUT_INVALID,
    ]),
    handleMouseEnterSection () {
      if (this.checklistDragItem === null) {
        this.isMouseInSectionRow = true
      }
    },
    handleMouseLeaveSection () {
      if (this.checklistDragItem === null) {
        this.isMouseInSectionRow = false
      }
    },
    handleRouteHash () {
      const hash = this.$route.hash.replace('#tk', '')
      this.EXPAND_ALL_SECTIONS().then(() => {
        this.$nextTick(() => {
          const element = document.getElementById('task-' + hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            highlightItem(element)
          }
        })
      })
    },
    checkMoveItemAllowed (targetItem: ChecklistItem) {
      if (!this.checklistDragItem || targetItem.isAwaitingValidation) {
        toast.error(this.t('moveItemAllowedError'))
        return
      }
      if(!this.isSectionDroppable){
        toast.error(this.t('moveItemAllowedError'))
        return
      }
      if(this.checklistDragItem.id === targetItem.id) {
        toast.error(this.t('moveItemAllowedError'))
        return
      }

      if (this.isDraggingSection) {
        const descendants = this.getAllDescendantIds(this.checklistDragItem as ChecklistSection)
        
        if (descendants.includes(targetItem.id)) {
          toast.error(this.t('moveItemAllowedError'))
          return
        }
      }
      
      const isDroppedAtSameLevel = this.checklistDragItem.depth === targetItem.depth + (this.isDraggingSection && targetItem.type === 'task' ? 1 : 0)
      const isDroppedAndDraggedSection = this.isDraggingSection && targetItem.type === 'title'

      const isDroppedInSameSection = targetItem.type === 'title' && this.hasDirectChildMatching(targetItem, this.checklistDragItem)
      
      if (this.prevChecklistDragItem?.id === targetItem.id && (isDroppedAtSameLevel && !isDroppedAndDraggedSection || isDroppedInSameSection)) {
        toast.error(this.t('moveItemAllowedError'))
        return
      }

      this.prepareMoveItem(targetItem)
    },
    countTasks (children: Array<ChecklistItem> = []): number {
      return children.reduce((count, child) => {
        if (child.type === 'task') {
          count++
        } else if (child.children) {
          count += this.countTasks(child.children)
        }
        return count
      }, 0)
    },
    onDeleteSection() {
      if(this.section.children.length > 0) {
        this.isDeleteSectionDialogOpen = true
      } else {
        this.prepareDeleteSection()
      }
    },
    onDropInSection (section: ChecklistSection) {
      this.checkMoveItemAllowed(section)
    },
    onDropAsFirstChildInSection (section: ChecklistSection) {
      this.prepareMoveItem(section, true, true)
    },
    onDropAfterSection (section: ChecklistSection) {
      this.prepareMoveItem(section, false)
    },
    onDropAfterTask (item: ChecklistItem) {
      this.checkMoveItemAllowed(item)
    },
    onLastSectionAdded() {
      const hasValidTitle = this.section.title && this.section.title.trim() !== ''
      
      if (this.checklistDetailsPending || hasValidTitle) {
        return
      }
      
      const newSectionElement = this.$refs['root'] as HTMLElement
      if (newSectionElement) {
        scrollToAddedItem(newSectionElement)
      }
      this.onRenameSection()
    },
    onRenameSection () {
      if (!this.isCurrentUserPm || this.checklistTitleInputInvalid) {
        return
      }
      this.isRenamingSection = true
    },
    onSectionClickOutside (e: MouseEvent) {
      const clickTarget = e.target as HTMLElement

      if(!this.isRenamingSection || clickTarget?.closest('.rename-button')) return

      this.prepareRenameSection()
    },
    onSelectSection () {
      if (this.isSectionSelected) {
        this.REMOVE_SECTION_FROM_SELECTION(this.section)
        this.REMOVE_SECTION_CHILDREN_FROM_SELECTION(this.section)
      } else {
        this.onSelectAll()
      }
    },
    onSelectAll () {
      this.ADD_SECTION_TO_SELECTION(this.section)
      this.ADD_SECTION_CHILDREN_TO_SELECTION(this.section)
    },
    onTitleInputFocusChanged (hasInputFocus: boolean) {
      const item = { type: 'title', id: this.section.id }

      if (hasInputFocus) {
        this.ADD_CHECKLIST_ITEM_BEING_EDITED(item)
      } else {
        this.REMOVE_CHECKLIST_ITEM_BEING_EDITED(item)
      }
    },
    async prepareDeleteSection () {
      try {
        await this.DELETE_SECTION({
          mnemo: this.roomMnemo,
          sectionId: this.section.id,
        })

        if (this.isSectionSelected) {
          this.REMOVE_SECTION_FROM_SELECTION(this.section)
        }

        if(this.isDeletionValidationEnabled) {
          toast.success(this.$t('project.project-settings.ProjectSettingsFeatures.checklist.pendingDeletion'))
        } else {
          toast.success(this.t('deleteSectionSuccess'))
        }
      } catch (e) {
        console.error(e)
        toast.error(this.t('deleteSectionError'))
      }
    },
    async prepareMoveItem(targetItem: ChecklistItem | ChecklistSection, withinSection = true, firstChild = false) {
      const moveData: moveDataType = {
        moveTo: {
          id: targetItem.id,
          type: targetItem.type,
        },
        selectedItems : [
          {
            id: this.checklistDragItem.id,
            type: this.checklistDragItem.type,
          },
        ],
        withinSection: withinSection,
        firstChild: firstChild,
      }
      const movedItemId = `${this.checklistDragItem.type}-${this.checklistDragItem.id}`
      const targetItemId = `${targetItem.type}-${targetItem.id}`
      try {
        await this.MOVE_ITEMS({
          mnemo: this.roomMnemo,
          data: moveData,
        })

        await this.GET_CHECKLIST(this.roomMnemo)
        scrollToMovedItemById(movedItemId, targetItemId)
        toast.success(this.t('moveItemsSuccess'))
      } catch (e) {
        console.error(e)
        toast.error(this.t('moveItemsError'))
      }
    },
    async prepareRenameSection () {
      if (!this.renamedSectionTitle) {
        toast.error(this.t('invalidTitleError'))
        return
      }
      if (this.section.title === this.renamedSectionTitle) {
        this.isRenamingSection = false
      } else {
        try {
          await this.EDIT_SECTION({
            data: {
              title: this.renamedSectionTitle,
            },
            mnemo: this.roomMnemo,
            sectionId: this.section.id,
          })
          
          this.lastSavedSectionTitle = this.section.title
          this.isRenamingSection = false
        } catch (e) {
          console.error(e)
          this.lastSavedSectionTitle = ''
        }
      }
    },
    toggleSectionExpansion () {
      if (this.isSectionExpanded) {
        this.COLLAPSE_SECTION(this.section.id)
      } else {
        this.EXPAND_SECTION(this.section.id)
      }
    },
    getAllDescendantIds (item: ChecklistSection): number[] {
      const result: number[] = []
      const traverse = (node: ChecklistSection) => {
        if (node.children && node.children.length) {
          for (const child of node.children) {
            result.push(child.id)
            if (child.type === 'title') {
              traverse(child as ChecklistSection)
            }
          }
        }
      }
      traverse(item)
      return result
    },
    hasDirectChildMatching(targetItem: ChecklistSection, draggedItem: ChecklistItem | ChecklistSection): boolean {
      return targetItem.children?.some(child => child.id === draggedItem.id && child.type === draggedItem.type) ?? false
    },
  },
})
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@import "@/project/checklist/table/checklist-table.scss";

.checklist-table-body-row {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: row-start / row-end;
}

.checklist-table-section {
  height: 52px;
  grid-column: row-start / row-end;
  border-bottom: 1px solid rgb(var(--v-theme-grey-100));
  cursor: pointer;
  user-select: none;

  &:has(.checklist-table-title-input:focus-within) {
    background-color: rgb(var(--v-theme-darkblue-50));
  }

  &:hover {
    background-color: rgb(var(--v-theme-grey-50));
  }
}

.checklist-table-section-inner {
  display: grid;
  grid-template-columns: var(--column-select-width) 1fr;
  height: 100%;
  left: 0;
  position: sticky;
  
  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    width: v-bind(rowWidth);
  }
}

.checklist-table-first-column {
  display: flex;
  align-items: center;
  column-gap: 2px;
}

.checklist-table-column-title {
  display: flex;
  align-items: center;
  column-gap: 8px;
  color: rgb(var(--v-theme-grey-900));
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  margin-left: v-bind(sectionDepthOffset);
  padding-inline: 8px;

  svg:focus{
    outline: none;
  }
}

.checklist-table-row-skeleton {
  width: v-bind(rowWidth);
  position: sticky;
  left: 0;
  grid-column: row-start / row-end;
}

.awaiting-delete-validation {
  pointer-events: none;
  background: rgb(var(--v-theme-grey-50));
  * {
    pointer-events: none;
    color: rgb(var(--v-theme-grey-300)) !important;
  }
  :deep(.ChecklistTableTaskStatus .v-chip__content) {
    color: rgb(var(--v-theme-grey-50)) !important;
  }
}

.drop-disabled {
  cursor: not-allowed !important;
  color: rgb(var(--v-theme-grey-400)) !important;
  background-color: rgb(var(--v-theme-grey-25)) !important;

  .checklist-table-column-title * {
    color: rgb(var(--v-theme-grey-400)) !important;
  }
  & * {
    pointer-events: none !important;
    cursor: not-allowed !important;
  }
}
.section-zone-highlight {
  transition: all 0.1s ease-in-out;
  border-radius: 8px;
  outline: 1px solid rgb(var(--v-theme-darkblue-500));
  outline-offset: -1px;
  background-color: rgb(var(--v-theme-darkblue-50)) !important;
}
</style>
